const FP_LS_KEY = "scribpost-dfp";
const FP_IDB_NAME = "scribpost-fp-db";
const FP_IDB_STORE = "fingerprints";

async function hashString(str: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

function getCanvasFingerprint(): string {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";
    ctx.textBaseline = "top";
    ctx.font = "14px 'Arial'";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("ScribPost.fp", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("ScribPost.fp", 4, 17);
    return canvas.toDataURL();
  } catch {
    return "";
  }
}

function getWebGLFingerprint(): string {
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl || !(gl instanceof WebGLRenderingContext)) return "";
    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
    if (!debugInfo) return "";
    const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || "";
    const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || "";
    return `${vendor}~${renderer}`;
  } catch {
    return "";
  }
}

function collectSignals(): string {
  const signals = [
    getCanvasFingerprint(),
    getWebGLFingerprint(),
    `${screen.width}x${screen.height}x${screen.colorDepth}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    navigator.language || "",
    navigator.platform || "",
    navigator.hardwareConcurrency?.toString() || "",
  ];
  return signals.join("|");
}

async function readFromIndexedDB(): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      const request = indexedDB.open(FP_IDB_NAME, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(FP_IDB_STORE)) {
          db.createObjectStore(FP_IDB_STORE);
        }
      };
      request.onsuccess = () => {
        try {
          const db = request.result;
          const tx = db.transaction(FP_IDB_STORE, "readonly");
          const store = tx.objectStore(FP_IDB_STORE);
          const get = store.get("dfp");
          get.onsuccess = () => resolve(get.result || null);
          get.onerror = () => resolve(null);
        } catch {
          resolve(null);
        }
      };
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function writeToIndexedDB(fp: string): Promise<void> {
  return new Promise((resolve) => {
    try {
      const request = indexedDB.open(FP_IDB_NAME, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(FP_IDB_STORE)) {
          db.createObjectStore(FP_IDB_STORE);
        }
      };
      request.onsuccess = () => {
        try {
          const db = request.result;
          const tx = db.transaction(FP_IDB_STORE, "readwrite");
          const store = tx.objectStore(FP_IDB_STORE);
          store.put(fp, "dfp");
          tx.oncomplete = () => resolve();
          tx.onerror = () => resolve();
        } catch {
          resolve();
        }
      };
      request.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

let cachedFp: string | null = null;

export async function getDeviceFingerprint(): Promise<string> {
  if (cachedFp) return cachedFp;

  const fromLS = localStorage.getItem(FP_LS_KEY);
  if (fromLS) {
    cachedFp = fromLS;
    writeToIndexedDB(fromLS).catch(() => {});
    return fromLS;
  }

  const fromIDB = await readFromIndexedDB();
  if (fromIDB) {
    cachedFp = fromIDB;
    localStorage.setItem(FP_LS_KEY, fromIDB);
    return fromIDB;
  }

  const signals = collectSignals();
  const fp = await hashString(signals);
  cachedFp = fp;

  localStorage.setItem(FP_LS_KEY, fp);
  writeToIndexedDB(fp).catch(() => {});

  return fp;
}
